import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Star, ChevronsUpDown, Globe, Menu, X } from 'lucide-react';
import { Topbar, useAppShell } from '@vnetwork/ui/layout';
import {
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@vnetwork/ui/primitives';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { useMyServices } from '@/api/services';
import { isWaapCapable } from '@/lib/waap-service';
import { paths } from '@/lib/paths';
import { useWaapCurrentPackage, WAAP_PLANS } from '@/api/waap/settings';
import { planPill, unknownPillProps } from './plan-pill';
import { usePinnedServices, useCurrentServiceLeaf } from './pinned-services';
import { UserMenu } from './UserMenu';
import { ProjectSwitcher } from './project-switcher/ProjectSwitcher';
import { DocsIcon, SupportIcon } from './shell-icons';
import { brand } from '@/config/brand';
import styles from './AppTopbar.module.css';

function Brand() {
  const { collapsed } = useAppShell();
  return (
    <Link to="/" className={styles.brandLink} data-collapsed={collapsed || undefined}>
      <img src={brand.assets.icon} alt="" width={24} height={24} className={styles.brandLogo} />
      {collapsed ? null : <span className={styles.brandName}>{brand.appName}</span>}
    </Link>
  );
}

/**
 * Lateral switch between the org's CDN orchestrators — rendered only inside a
 * CDN console (/services/cdn/:serviceId/*). Switching preserves the current
 * sub-page (Cache stays Cache on the next orchestrator), Cloudflare-style.
 */
function CdnServiceSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/services\/cdn\/([0-9a-f-]{36})(\/.*)?$/);
  const activeId = match?.[1];
  const subPath = match?.[2] ?? '';
  const { data } = useCdnUmbrellas(undefined, { enabled: !!activeId });
  const umbrellas = data?.umbrellas ?? [];

  // Hidden outside a console, and pointless with a single orchestrator.
  if (!activeId || umbrellas.length < 2) return null;
  const active = umbrellas.find((u) => u.service_id === activeId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={styles.switcher}>
          <Globe size={14} color="var(--vn-fg-muted)" />
          {active?.service_name ?? 'CDN'}
          <ChevronsUpDown size={12} strokeWidth={2.5} color="var(--vn-fg-subtle)" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>CDN orchestrators</DropdownMenuLabel>
        {umbrellas.slice(0, 8).map((u) => (
          <DropdownMenuItem
            key={u.service_id}
            disabled={u.service_id === activeId}
            onSelect={() => navigate(`/services/cdn/${u.service_id}${subPath}`)}
          >
            {u.service_name}
            {u.is_shared ? ' · Shared' : ''}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/services/cdn')}>
          View all orchestrators
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Pin/unpin the service the current route is inside — the pinned set drives the
 * "Pinned" sidebar section and the dashboard block. Hidden outside a service
 * console (nothing to pin).
 */
function PinCurrentServiceButton() {
  const { pathname } = useLocation();
  const current = useCurrentServiceLeaf(pathname);
  const { isPinned, toggle } = usePinnedServices();
  if (!current) return null;
  const pinned = isPinned(current.id);
  return (
    <button
      type="button"
      aria-label={pinned ? 'Unpin service' : 'Pin service'}
      title={pinned ? 'Unpin from sidebar & dashboard' : 'Pin to sidebar & dashboard'}
      className={`${styles.iconBtn} ${styles.desktopOnly}`}
      onClick={() => toggle(current.id)}
    >
      <Star size={16} color="var(--vn-fg-subtle)" fill={pinned ? 'currentColor' : 'none'} />
    </button>
  );
}

/**
 * Shows the account's WAAP subscription tier (Free / Standard / Pro / Custom).
 * The plan is per-WAAP-service, so we resolve the org's WAAP service and read its
 * active package. Hidden when the account has no WAAP service or no package set —
 * never a hardcoded placeholder; a neutral dash when the package could not be read
 * (`plan-pill.ts`). Clicking opens the plan settings.
 */
function WaapPackageBadge() {
  const navigate = useNavigate();
  const { data: services = [] } = useMyServices();
  // WAAP is the `cobra` provider — authoritative detection (a service's nav route
  // may fall back to the category when its scope isn't in the taxonomy).
  const waap = useMemo(() => services.find(isWaapCapable), [services]);
  const { data: packageId, isError } = useWaapCurrentPackage(waap?.id);
  const pill = planPill({
    hasService: !!waap,
    packageId,
    isError,
    planName: (id) => WAAP_PLANS.find((p) => p.id === id)?.name ?? 'Custom',
  });
  if (!waap || pill.kind === 'hidden') return null;
  if (pill.kind === 'unknown') {
    return (
      <button
        type="button"
        onClick={() => navigate(`${paths.waap.service(waap.id)}/settings`)}
        className={`${styles.planPill} ${styles.planPillFree} ${styles.desktopOnly}`}
        {...unknownPillProps()}
      >
        —
      </button>
    );
  }
  const { name, free: isFree } = pill;
  return (
    <button
      type="button"
      onClick={() => navigate(`${paths.waap.service(waap.id)}/settings`)}
      className={`${styles.planPill} ${isFree ? styles.planPillFree : styles.planPillPaid} ${styles.desktopOnly}`}
      title="WAAP subscription — manage plan"
    >
      {name}
    </button>
  );
}

function TopbarActions({ docsOpen, onToggleDocs }: { docsOpen: boolean; onToggleDocs: () => void }) {
  const navigate = useNavigate();
  return (
    <div className={styles.actions}>
      {/* Documentation / Support — icon-only on mobile (label hidden, icon kept)
          so they stay reachable, not dropped. */}
      {brand.features.documentation && (
        <button
          type="button"
          className={styles.topbarLink}
          data-open={docsOpen || undefined}
          onClick={onToggleDocs}
          aria-label="Documentation"
        >
          <DocsIcon size={15} />
          <span className={styles.desktopOnly}>Documentation</span>
        </button>
      )}
      {brand.features.support && (
        <button
          type="button"
          className={styles.topbarLink}
          onClick={() => navigate('/support')}
          aria-label="Support"
        >
          <SupportIcon size={15} />
          <span className={styles.desktopOnly}>Support</span>
        </button>
      )}
      <UserMenu />
    </div>
  );
}

interface AppTopbarProps {
  mobileNavOpen: boolean;
  onToggleMobileNav: () => void;
  docsOpen: boolean;
  onToggleDocs: () => void;
}

export function AppTopbar({ mobileNavOpen, onToggleMobileNav, docsOpen, onToggleDocs }: AppTopbarProps) {
  return (
    <Topbar
      brand={
        <>
          {/* Hamburger — mobile only, on the LEFT (standard placement) */}
          <IconButton
            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
            variant="ghost"
            onClick={onToggleMobileNav}
            className={styles.hamburger}
          >
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </IconButton>
          <Brand />
        </>
      }
      actions={<TopbarActions docsOpen={docsOpen} onToggleDocs={onToggleDocs} />}
    >
      {/* Center items — hide lower-priority items on mobile */}
      <span className={styles.topbarCenter}>
        <ProjectSwitcher />
        <CdnServiceSwitcher />
        <PinCurrentServiceButton />
        <WaapPackageBadge />
      </span>
    </Topbar>
  );
}
